import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, RefreshCw, X, Copy, ShieldCheck, ShieldOff, Mail, AlertTriangle, CheckCircle2, XCircle, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ResetLinkButton, BulkInviteDialog } from "@/components/AdminUserTools";
import { UserAccessDialog, DeptPicker } from "@/components/UserAccessDialog";
import { KeyRound } from "lucide-react";
import { DemoCleanupDialog } from "@/components/DemoCleanupDialog";

const DeptChips = ({ ids, names, testid }) => (
  <div className="flex flex-wrap gap-1 mt-1" data-testid={testid}>
    {ids.length === 0 ? <span className="text-[11px] text-slate-500">No department</span> :
      ids.map((id) => <span key={id} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-800 border border-blue-100">{names[id] || "?"}</span>)}
  </div>
);

const ModulesOff = ({ modules }) => {
  const off = Object.entries(modules || {}).filter(([, v]) => !v).map(([k]) => k);
  return off.length ? <div className="text-[10px] text-amber-700 mt-1">No access: {off.map((k) => k[0].toUpperCase() + k.slice(1)).join(", ")}</div> : null;
};

const ROLE_LABELS = {
  admin: "Administrator",
  top_management: "Top Management",
  employee: "Employee",
};
const ROLE_BADGE = {
  admin: "bg-rose-50 text-rose-700 border-rose-200",
  top_management: "bg-indigo-50 text-indigo-700 border-indigo-200",
  employee: "bg-slate-50 text-slate-700 border-slate-200",
};

export default function AdminUsers() {
  const { user } = useAuth();
  const gmailConfigured = !!(user?.email_configured ?? user?.gmail_configured);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("employee");
  const [creating, setCreating] = useState(false);
  const [lastCreatedInvite, setLastCreatedInvite] = useState(null); // {url, email_sent, email}
  const [departments, setDepartments] = useState([]);
  const [inviteDepts, setInviteDepts] = useState([]);
  const [accessFor, setAccessFor] = useState(null);
  const isAdmin = user?.role === "admin";
  const deptNames = Object.fromEntries(departments.map((d) => [d.id, d.name]));
  const myDepts = isAdmin ? departments : departments.filter((d) => (user?.department_ids || []).includes(d.id));

  const load = async () => {
    setLoading(true);
    try {
      const [u, i, d] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/invitations"),
        api.get("/departments"),
      ]);
      setUsers(u.data);
      setInvitations(i.data);
      setDepartments(d.data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/users/invite", { name: name.trim(), email: email.trim(), role, department_ids: role === "admin" ? [] : inviteDepts });
      const link = data.invite_url || `${window.location.origin}/invite/${data.token}`;
      await navigator.clipboard?.writeText(link).catch(() => {});
      setLastCreatedInvite({ url: link, email_sent: data.email_sent, email: data.email });
      toast.success(data.email_sent ? "Invite emailed & link copied" : "Invite created — link copied (email not sent)");
      setName(""); setEmail(""); setRole("employee"); setInviteDepts([]); setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setCreating(false);
    }
  };

  const changeRole = async (id, newRole) => {
    try { await api.post(`/admin/users/${id}/role`, { role: newRole }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const toggleActive = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/${u.is_active ? "deactivate" : "reactivate"}`);
      toast.success(u.is_active ? "User deactivated" : "User reactivated");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const unlockUser = async (u) => {
    try { await api.post(`/admin/users/${u.id}/unlock`); toast.success(`${u.name || u.email} unlocked`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const resendInvite = async (id) => {
    try { await api.post(`/admin/invitations/${id}/resend`); toast.success("Invitation resent"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const revokeInvite = async (id) => {
    try { await api.delete(`/admin/invitations/${id}`); toast.success("Invitation revoked"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const copyInviteLink = async (inv) => {
    const link = inv.invite_url || `${window.location.origin}/invite/${inv.token}`;
    await navigator.clipboard?.writeText(link).catch(() => {});
    toast.success("Invite link copied");
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Team</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" data-testid="admin-users-title">{isAdmin ? "Users & invitations" : "My team"}</h1>
          {!isAdmin && <p className="text-sm text-slate-500 mt-1">Everyone in your departments and the Top Management users who report to you.</p>}
        </div>
        <div className="flex gap-2">
          {isAdmin && <DemoCleanupDialog onDone={load} />}
          <BulkInviteDialog onDone={load} isAdmin={isAdmin} departments={myDepts} />
          <Button data-testid="invite-user-btn" onClick={() => setDialogOpen(true)} className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
            <UserPlus className="w-4 h-4" /> Invite user
          </Button>
        </div>
      </div>

      {/* Gmail-not-configured banner — surfaces the actual reason invite emails fail */}
      {!gmailConfigured && (
        <div data-testid="gmail-warning-banner" className="mb-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">Invite emails are not being delivered.</div>
            <div className="text-xs text-amber-800 mt-0.5">
              Your Gmail app password isn't configured, so invitees won't receive an automated email.
              You can still <b>Copy invite link</b> below and share it via WhatsApp, Slack, or your own email.
            </div>
          </div>
          <Link to="/settings" className="shrink-0">
            <Button data-testid="configure-gmail-btn" size="sm" variant="outline" className="border-amber-300 text-amber-900 hover:bg-amber-100">
              Configure Gmail
            </Button>
          </Link>
        </div>
      )}

      {/* Last-created invite: prominent copy-again panel so admins have zero-friction fallback */}
      {lastCreatedInvite && (
        <div data-testid="last-invite-panel" className={`mb-4 flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${lastCreatedInvite.email_sent ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-sky-200 bg-sky-50 text-sky-900"}`}>
          {lastCreatedInvite.email_sent ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              {lastCreatedInvite.email_sent
                ? `Invite emailed to ${lastCreatedInvite.email}`
                : `Invite created for ${lastCreatedInvite.email} — email couldn't be sent`}
            </div>
            <div className="text-xs mono truncate mt-0.5">{lastCreatedInvite.url}</div>
          </div>
          <Button
            data-testid="last-invite-copy"
            size="sm"
            variant="outline"
            onClick={async () => { await navigator.clipboard?.writeText(lastCreatedInvite.url).catch(() => {}); toast.success("Link copied"); }}
            className="shrink-0 gap-1.5"
          >
            <Copy className="w-3.5 h-3.5" /> Copy link
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setLastCreatedInvite(null)} className="shrink-0"><X className="w-4 h-4" /></Button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold">Team members {users.length > 0 && <span className="text-slate-400 font-normal">({users.length})</span>}</div>
        {loading ? <div className="p-6 text-center text-slate-500 text-sm">Loading…</div> : (
          <ul className="divide-y divide-slate-200">
            {users.map((u) => (
              <li key={u.id} data-testid={`user-row-${u.id}`} className="px-5 py-3 grid grid-cols-12 gap-2 items-center">
                <div className="col-span-3 min-w-0">
                  <div className="font-medium truncate">{u.name}{u.id === user?.id && <span className="ml-1 text-[10px] text-slate-400">(you)</span>}</div>
                  <div className="text-xs text-slate-500 truncate mono">{u.email}</div>
                  {u.role !== "admin" && <DeptChips ids={u.department_ids || []} names={deptNames} testid={`user-depts-${u.id}`} />}
                  {u.manager_name && <div className="text-[11px] text-slate-600 mt-0.5" data-testid={`user-manager-${u.id}`}>Reports to {u.manager_name}</div>}
                  {u.role !== "admin" && <ModulesOff modules={u.modules} />}
                </div>
                <div className="col-span-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={!u.can_manage}>
                    <SelectTrigger data-testid={`user-role-select-${u.id}`} className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isAdmin && <SelectItem value="admin">Administrator</SelectItem>}
                      <SelectItem value="top_management">Top Management</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 text-center">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${ROLE_BADGE[u.role] || ROLE_BADGE.employee}`}>{ROLE_LABELS[u.role]}</span>
                </div>
                <div className="col-span-2">
                  {u.is_active ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-100 text-slate-500 border border-slate-200 inline-flex items-center gap-1">
                      <ShieldOff className="w-3 h-3" /> Deactivated
                    </span>
                  )}
                  {u.is_locked && (
                    <span data-testid={`user-locked-badge-${u.id}`} className="ml-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-right flex justify-end gap-1 whitespace-nowrap">
                  {u.can_manage && (
                    <>
                      {u.role !== "admin" && <Button data-testid={`user-access-${u.id}`} size="sm" variant="outline" className="gap-1" onClick={() => setAccessFor(u)}><KeyRound className="w-3.5 h-3.5" />Access</Button>}
                      <ResetLinkButton user={u} />
                      {u.is_locked && <Button data-testid={`user-unlock-${u.id}`} size="sm" variant="outline" onClick={() => unlockUser(u)}>Unlock</Button>}
                      <Button data-testid={`user-toggle-${u.id}`} size="sm" variant="outline" onClick={() => toggleActive(u)}>{u.is_active ? "Deactivate" : "Reactivate"}</Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold flex items-center gap-2">
          <Mail className="w-4 h-4" /> Invitations {invitations.length > 0 && <span className="text-slate-400 font-normal" data-testid="invitations-pending-count">({invitations.filter((i) => i.is_active && !i.accepted_at).length} pending)</span>}
        </div>
        {invitations.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">No invitations sent yet.</div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {invitations.map((inv) => {
              const status = inv.accepted_at ? "accepted" : !inv.is_active ? "revoked" : new Date(inv.expires_at) < new Date() ? "expired" : "pending";
              return (
                <li key={inv.id} data-testid={`invite-row-${inv.id}`} className="px-5 py-3 grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4 min-w-0">
                    <div className="font-medium truncate">{inv.name}</div>
                    <div className="text-xs text-slate-500 truncate mono">{inv.email}</div>
                  </div>
                  <div className="col-span-2 text-xs">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${ROLE_BADGE[inv.role]}`}>{ROLE_LABELS[inv.role]}</span>
                  </div>
                  <div className="col-span-2 text-xs mono text-slate-500">Expires {inv.expires_at?.slice(0, 10)}</div>
                  <div className="col-span-2 text-xs flex items-center gap-1">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                      status === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      status === "revoked" ? "bg-slate-100 text-slate-500 border-slate-200" :
                      status === "expired" ? "bg-rose-50 text-rose-700 border-rose-200" :
                      "bg-amber-50 text-amber-700 border-amber-200"}`}>{status}</span>
                    {status === "pending" && (
                      inv.email_sent === true ? (
                        <span data-testid={`invite-delivery-${inv.id}`} title="Email delivered" className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-0.5">
                          <CheckCircle2 className="w-3 h-3" /> Sent
                        </span>
                      ) : inv.email_sent === false ? (
                        <span data-testid={`invite-delivery-${inv.id}`} title={inv.email_error || "Email not sent — share link manually"} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-rose-50 text-rose-700 border-rose-200 inline-flex items-center gap-0.5">
                          <XCircle className="w-3 h-3" /> Not sent
                        </span>
                      ) : null
                    )}
                  </div>
                  <div className="col-span-2 text-right flex justify-end gap-1">
                    {status === "pending" && (
                      <>
                        <Button data-testid={`invite-copy-${inv.id}`} size="sm" variant="outline" onClick={() => copyInviteLink(inv)} className="gap-1.5 h-7">
                          <Copy className="w-3.5 h-3.5" /> Copy link
                        </Button>
                        <Button data-testid={`invite-resend-${inv.id}`} size="sm" variant="ghost" onClick={() => resendInvite(inv.id)} title="Resend email"><RefreshCw className="w-4 h-4" /></Button>
                        <Button data-testid={`invite-revoke-${inv.id}`} size="sm" variant="ghost" onClick={() => revokeInvite(inv.id)} title="Revoke"><X className="w-4 h-4 text-rose-600" /></Button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invite a user</DialogTitle></DialogHeader>
          {!gmailConfigured && (
            <div data-testid="invite-dialog-warning" className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div>
                Gmail isn't configured — the invitee <b>won't get an email</b>. After you click Send, the invite link will be copied to your clipboard so you can share it manually.
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Full name</Label>
              <Input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Doe" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@company.com" className="mt-1 mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isAdmin && <SelectItem value="admin">Administrator</SelectItem>}
                  <SelectItem value="top_management">Top Management</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {role !== "admin" && (isAdmin || myDepts.length > 1) && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{role === "employee" ? "Department" : "Departments"}</Label>
                <div className="mt-1"><DeptPicker departments={myDepts} value={inviteDepts} onChange={setInviteDepts} multi={role !== "employee"} testid="invite-departments" /></div>
              </div>
            )}
            {!isAdmin && myDepts.length === 1 && <div className="text-xs text-slate-500" data-testid="invite-dept-auto">Joins your department: <b>{myDepts[0].name}</b></div>}
            {!isAdmin && myDepts.length === 0 && <div className="text-xs text-amber-700" data-testid="invite-dept-missing">Ask the administrator to give you a department before inviting.</div>}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="invite-submit" onClick={invite} disabled={creating} className="bg-blue-700 hover:bg-blue-800 text-white">
              {creating ? "Sending…" : (gmailConfigured ? "Send invitation" : "Create invite & copy link")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UserAccessDialog target={accessFor} me={user} departments={departments} users={users}
        onClose={() => setAccessFor(null)} onSaved={() => { setAccessFor(null); load(); }} />
    </div>
  );
}
